import { useState, useEffect } from 'react'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import DateInput from 'components/Inputs/DateInput'
import ScheduleOptionList from 'components/Lists/ScheduleOptionList'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { IoWarning } from 'react-icons/io5'
import { DateTime } from 'luxon'

import {
  SubContainer,
  Row,
  FilterColumn,
  TimeTablesGrid,
  Button,
  SubTitle,
  InfoText
} from '../styles'

export default function TimeTablesForm ({
  schedule = {},
  onProceed: handleProceed = () => null,
  onScheduleOptionSelected: handleScheduleOptionSelected = () => null
}) {
  const now = DateTime.now ()
  const [times, setTimes] = useState ([])
  const [loadingDates, setLoadingDates] = useState (true)
  const [loadingTimes, setLoadingTimes] = useState (false)
  const [workingDays, setWorkingDays] = useState ([])
  const [exception, setException] = useState (null)
  const [date, setDate] = useState (schedule.date ?? now.toISODate ())
  const { business, service } = schedule
  
  useEffect (() => {
    getAvaliableDates ()
  }, [])

  useEffect (() => {
    getTimes (date)
  }, [date])

  const getAvaliableDates = async () => {
    setLoadingDates (true)

    const url = `/businesses/${business.id}/working_days?accepts_online=true`

    try {
      const { data: workingDays } = await cutileiApi.get (url, { anonymous: true })
      setWorkingDays (workingDays)
      await getTimes ()
    } catch (error) {
      logApiError (error)
    } finally {
      setLoadingDates (false)
    }
  }

  const getTimes = async (selectedDate = date) => {
    setException (null)
    setLoadingTimes (true)

    const filters = `date=${selectedDate}&schedule_id=${schedule.id}`
                  + `&service_id=${service.id}&online_schedule=true`

    try {
      const { data } = await cutileiApi.get (
        `/businesses/${business.id}/free_schedules?${filters}`,
        { anonymous: true }
      )

      setTimes (selectedDate === now.toISODate () ? data.filter (timeWindow => (
        DateTime.fromFormat (timeWindow, 'H:mm') > now
      )) : data)
    } catch (error) {
      logApiError (error)

      /*
        `error.response.data` direto quebrava dentro do próprio catch quando a requisição
        falhava sem resposta — offline, timeout, cancelamento —, e a tela ficava sem horário
        e sem aviso. O spread preserva o `options` das alternativas; a mensagem vem do
        helper, que sempre devolve texto.
      */
      setException({
        ...error.response?.data,
        message: getApiErrorMessage(error)
      })
    } finally {
      setLoadingTimes (false)
    }
  }

  const getDisabledDates = ({ date }) => {
    const parsedDate = DateTime.fromJSDate (date)

    return (
      parsedDate < now.minus ({days: 1}) ||
      !workingDays.some (day => day.iso_number === parsedDate.weekday)
    )
  }

  const handleOptionSelected = optionData => {
    if (optionData.combinationOptions) {
      handleScheduleOptionSelected ({
        date, combinationOptions: optionData.combinationOptions
      })
    } else if (optionData.scheduleOption) {
      handleScheduleOptionSelected ({
        date, scheduleOption: optionData.scheduleOption
      })
    }
  }

  return (
    <SubContainer>
      <SubTitle>Escolha data e horário</SubTitle>
      <Row style={{marginTop: 10}}>
        <FilterColumn>
          <DateInput
            value={date}
            onChange={setDate}
            style={{width: '100%'}}
            disabledDates={getDisabledDates}
          />
        </FilterColumn>
      </Row>
      {loadingDates || loadingTimes ? <Loading style={{marginTop: 20}}/> : (
        exception ? (
          <SubContainer style={{marginTop: 20}}>
            {exception.options && (
              <Row>
                <IoWarning size={26} color='#FFC100'/>
                <SubTitle>Atenção!</SubTitle>
              </Row>
            )}
            <InfoText style={{marginTop: 10}}>
              {exception.message}
            </InfoText>
            {exception.options && (
              <ScheduleOptionList
                options={exception.options}
                onSelect={handleOptionSelected}
              />
            )}
          </SubContainer>
        ) : times.length > 0 ? (
          <TimeTablesGrid>
            {times.map ((time, index) => (
              <Button key={index} onClick={() => handleProceed ({ date, time })}>
                {time}
              </Button>
            ))}
          </TimeTablesGrid>
        ) : (
          /*
            Lista vazia sem erro é caso real: a API devolve os horários do dia e este
            formulário descarta os que já passaram. Sem este ramo a grade renderizava vazia,
            sem dizer nada, e foi o que o salão relatou.
          */
          <InfoText style={{marginTop: 20}}>
            Não há horários disponíveis neste dia.
          </InfoText>
        )
      )}
    </SubContainer>
  )
}
